/**
 * RichTextEditor — TipTap-based rich text editor for a single newsletter section.
 * All HTML output is sanitized via DOMPurify before being stored.
 */
import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import FontFamily from '@tiptap/extension-font-family';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, List, ListOrdered,
  Heading1, Heading2, Heading3,
  Highlighter, Palette,
} from 'lucide-react';
import { sanitizeHtml } from '../lib/sanitize';
import askPrompt from '../../../components/em/askPrompt';

const FONT_FAMILIES = [
  'Inter, sans-serif',
  'Arial, sans-serif',
  'Georgia, serif',
  'Times New Roman, serif',
  'Courier New, monospace',
  'Trebuchet MS, sans-serif',
];

const ToolbarButton = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onClick={(e) => { e.preventDefault(); onClick?.(); }}
    title={title}
    style={{
      background: active ? '#f54b01' : 'transparent',
      color: active ? '#fff' : '#374151',
      border: 'none',
      borderRadius: 4,
      padding: '4px 6px',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background 0.15s',
    }}
  >
    {children}
  </button>
);

function Toolbar({ editor }) {
  if (!editor) return null;

  const setLink = async () => {
    const url = await askPrompt('Enter URL', editor.getAttributes('link').href || 'https://');
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
  };

  const insertImage = async () => {
    const url = await askPrompt('Enter image URL', '', { placeholder: 'https://…' });
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const isBold   = editor.isActive('bold');
  const isItalic = editor.isActive('italic');
  const isUnder  = editor.isActive('underline');
  const isStrike = editor.isActive('strike');
  const isLink   = editor.isActive('link');

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 2,
      padding: '6px 10px', borderBottom: '1px solid var(--em-border)',
      background: 'var(--em-bg-head)',
    }}>
      {/* Headings */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="H1"><Heading1 size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="H2"><Heading2 size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="H3"><Heading3 size={15}/></ToolbarButton>

      <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }}/>

      {/* Inline */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={isBold} title="Bold"><Bold size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={isItalic} title="Italic"><Italic size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={isUnder} title="Underline"><UnderlineIcon size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={isStrike} title="Strikethrough"><Strikethrough size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight"><Highlighter size={15}/></ToolbarButton>

      <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }}/>

      {/* Align */}
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Center"><AlignCenter size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify size={15}/></ToolbarButton>

      <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }}/>

      {/* Lists */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List size={15}/></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list"><ListOrdered size={15}/></ToolbarButton>

      <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }}/>

      {/* Media */}
      <ToolbarButton onClick={setLink} active={isLink} title="Link"><LinkIcon size={15}/></ToolbarButton>
      <ToolbarButton onClick={insertImage} title="Image"><ImageIcon size={15}/></ToolbarButton>

      <span style={{ width: 1, background: '#e5e7eb', margin: '0 4px' }}/>

      {/* Color */}
      <label title="Text color" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
        <Palette size={15} color="#374151"/>
        <input type="color" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />
      </label>

      {/* Font family */}
      <select
        onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
        style={{ fontSize: 12, border: '1px solid var(--em-border)', borderRadius: 4, padding: '2px 4px', color: '#374151' }}
        defaultValue=""
      >
        <option value="" disabled>Font…</option>
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f}>{f.split(',')[0]}</option>
        ))}
      </select>
    </div>
  );
}

export function RichTextEditor({ content, onChange, placeholder = 'Start typing…', readOnly = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener' } }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = sanitizeHtml(editor.getHTML(), 'preview');
      onChange?.(html);
    },
  });

  // Sync content when section changes (e.g. version restore)
  const prevContent = React.useRef(content);
  React.useEffect(() => {
    if (!editor) return;
    if (content !== prevContent.current && content !== editor.getHTML()) {
      editor.commands.setContent(content || '', false);
    }
    prevContent.current = content;
  }, [content, editor]);

  return (
    <div style={{ border: '1px solid var(--em-border)', borderRadius: 8, overflow: 'hidden', background: 'var(--em-bg-card)' }}>
      {!readOnly && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        style={{ minHeight: 120, padding: 12, outline: 'none' }}
      />
      <style>{`
        .ProseMirror { outline: none; }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror h1 { font-size: 2em; font-weight: 700; margin-bottom: 0.5em; }
        .ProseMirror h2 { font-size: 1.5em; font-weight: 600; margin-bottom: 0.4em; }
        .ProseMirror h3 { font-size: 1.25em; font-weight: 600; margin-bottom: 0.3em; }
        .ProseMirror ul { padding-left: 1.5em; list-style-type: disc; }
        .ProseMirror ol { padding-left: 1.5em; list-style-type: decimal; }
        .ProseMirror ul ul { list-style-type: circle; }
        .ProseMirror li { margin-bottom: 0.2em; }
        .ProseMirror blockquote { border-left: 3px solid var(--em-border); padding-left: 1em; color: #6b7280; }
        .ProseMirror a { color: #f54b01; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; border-radius: 4px; }
      `}</style>
    </div>
  );
}
